import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import usePoll from "../../hooks/usePoll";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { shortLocationName } from "../../utils/locationName";
import { formatPhoneDisplay } from "../../utils/phone";
import TextPhotos from "./TextPhotos";
import "./MessagesMenu.css";

/**
 * Global Messages button + slide-out panel, mounted in the admin top bar
 * (left of the inbox bell) so customer messages are reachable from any page.
 *
 * This is the seed of Chariot's multi-source messaging platform. Two feeds
 * merge here, newest first, each polled on its own:
 *   - incoming Shopmonkey customer messages (moved here from the Poseidon
 *     Live page header — same endpoints, same read/unread semantics). Opening
 *     one goes to the order in Shopmonkey.
 *   - customers' texts to the Chariot line (GET /v1/customer-texts,
 *     chariot-backend services/customerTexts.js): every one, on an order or
 *     not, from a known number or not, unread until someone reads it here or
 *     on its order. A new one refreshes this feed at once (socket
 *     `customer:message`). Opening one shows the whole text and its photos,
 *     with a way to its order or customer; "Mark all read" clears them.
 *     For people who read customer threads AND can open orders or customers
 *     (chariot-backend utils/orderAudience.js readsCustomerTexts).
 *
 * writerScope mirrors the profile-dropdown "All / Mine" Poseidon Live toggle:
 * "own" narrows the feeds to the signed-in writer's orders. Users without
 * shopmonkey.view_all_writers are own-scoped server-side no matter what.
 */

// "First L." — matches the writer display convention used across Poseidon Live.
function shortName(fullName) {
  if (!fullName) return "—";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

const keyOf = (m) => `${m.source}:${m.message_id}`;
const PREVIEW = 120;

// One feed's messages, or null when it couldn't be read.
async function readFeed(path, source) {
  try {
    const res = await fetchWithAuth(path);
    const json = await res.json();
    if (!res.ok || (source === "shopmonkey" && !json.success)) return null;
    return (json.messages || []).map((m) => ({ ...m, source }));
  } catch {
    return null;
  }
}

function when(at) {
  const d = new Date(at);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

export default function MessagesMenu({ writerScope = "all" }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  // Each feed's own gate — the button hides when both fetches would 403.
  const seesShopmonkey = can("shopmonkey.access");
  const seesTexts = (can("orders.view_money") || can("messages.send_sms") || can("messages.send_email"))
    && (can("orders.access") || can("customers.access"));
  const canSeeMessages = seesShopmonkey || seesTexts;

  const [open, setOpen] = useState(false);
  const [smMessages, setSmMessages] = useState([]);
  const [texts, setTexts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const scope = writerScope === "own" ? "?scope=own" : "";

  const messages = useMemo(
    () => [...smMessages, ...texts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [smMessages, texts],
  );
  const unreadCount = messages.filter((m) => !m.is_read).length;
  const unreadTexts = texts.filter((m) => !m.is_read).length;

  // Each feed keeps what it showed last when a read fails; the badge catches up next poll.
  const fetchSm = useCallback(async () => {
    const list = await readFeed(`/admin/shopmonkey/messages${scope}`, "shopmonkey");
    if (!list) return false; // usePoll backs off while the API struggles
    setSmMessages(list);
    setLoaded(true);
    setLastRefreshed(new Date());
    return true;
  }, [scope]);
  const fetchTexts = useCallback(async () => {
    const list = await readFeed(`/v1/customer-texts${scope}`, "chariot");
    if (!list) return false;
    setTexts(list);
    setLoaded(true);
    setLastRefreshed(new Date());
    return true;
  }, [scope]);

  // Fetch on mount + poll every 120 s; a scope flip refetches immediately and
  // restarts the polls. A new text to the Chariot line refreshes that feed at
  // once (through its poll's trigger: never two reads at a time), and on
  // returning to the tab.
  usePoll(fetchSm, 120000, { enabled: seesShopmonkey, resetKey: fetchSm });
  const refreshTexts = usePoll(fetchTexts, 120000, { enabled: seesTexts, resetKey: fetchTexts, refreshOnVisible: true });
  const onNewText = useCallback(() => {
    if (seesTexts && !document.hidden) refreshTexts();
  }, [seesTexts, refreshTexts]);
  useSocketEvent("customer:message", onNewText);

  const patch = (msg, json) => {
    const set = msg.source === "chariot" ? setTexts : setSmMessages;
    set((prev) => prev.map((m) => (keyOf(m) === keyOf(msg)
      ? { ...m, is_read: json.is_read ?? true, acknowledged_by_name: json.acknowledged_by_name, acknowledged_at: json.acknowledged_at }
      : m)));
  };

  // Read for everyone (or unread again): Shopmonkey's endpoints, or the
  // Chariot text's own.
  const setRead = async (msg, read) => {
    try {
      const res = msg.source === "chariot"
        ? await fetchWithAuth(`/v1/customer-texts/${msg.message_id}/read`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ read }),
          })
        : await fetchWithAuth(
            `/admin/shopmonkey/messages/${msg.message_id}/${read && !msg.is_read ? "mark-read" : "toggle-read"}`,
            { method: "POST" }
          );
      const json = await res.json();
      if (res.ok && json.success !== false) patch(msg, json);
    } catch {
      /* silent — the next poll shows the real state */
    }
  };

  const markAllTexts = async () => {
    try {
      const res = await fetchWithAuth("/v1/customer-texts/read-all", { method: "POST" });
      if (res.ok) await refreshTexts();
    } catch {
      /* silent — the next poll shows the real state */
    }
  };

  // A Chariot text opens in place (the whole text, its photos, the way to its
  // order or customer); a Shopmonkey one opens the order in Shopmonkey.
  const openMessage = (msg) => {
    if (!msg.is_read) setRead(msg, true);
    if (msg.source === "chariot") {
      setExpanded((k) => (k === keyOf(msg) ? null : keyOf(msg)));
    } else if (msg.order_id) {
      window.open(`https://app.shopmonkey.cloud/order/${msg.order_id}`, "_blank");
    }
  };

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  if (!canSeeMessages) return null;

  return (
    <div className="msg-menu">
      <button
        className="icon-button"
        onClick={() => setOpen((p) => !p)}
        title="Messages"
        aria-label="Open messages"
      >
        {/* Chat bubble (SendMessage_Icon), not the envelope Messages_Icon —
            the envelope reads as email, and its wide flap disappears behind
            the badge. Count caps at two digits (100+ shows "99", no plus) so
            the pill never grows wider than the icon it sits on. */}
        <span
          className="top-bar-icon"
          style={{ "--icon-url": "url(/images/Icons/SVG/SendMessage_Icon.svg)" }}
        />
        {unreadCount > 0 && (
          <span className="msg-badge">
            {unreadCount > 99 ? "99" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div className="msg-overlay" onClick={() => setOpen(false)} />
            <div className="msg-panel">
              <div className="msg-panel-header">
                <h3>Messages</h3>
                <div className="msg-panel-meta">
                  {unreadTexts > 0 && (
                    <button type="button" className="msg-mark-all" onClick={markAllTexts} title="Mark every unread Chariot text as read, for everyone">
                      Mark texts read
                    </button>
                  )}
                  {lastRefreshed && (
                    <span className="msg-panel-time">
                      Updated{" "}
                      {lastRefreshed.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                  <button
                    className="msg-panel-close"
                    onClick={() => setOpen(false)}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="msg-panel-body">
                {!loaded && messages.length === 0 ? (
                  <p className="empty-state">Loading messages…</p>
                ) : messages.length === 0 ? (
                  <p className="empty-state">No messages yet.</p>
                ) : (
                  messages.map((msg) => {
                    const isOpen = expanded === keyOf(msg);
                    const long = msg.text && msg.text.length > PREVIEW;
                    return (
                      <div
                        key={keyOf(msg)}
                        className={`msg-item${msg.is_read ? " read" : " unread"}`}
                      >
                        <div
                          className="msg-item-body"
                          onClick={() => openMessage(msg)}
                        >
                          <div className="msg-item-top">
                            <span className="msg-customer">
                              {msg.customer_name ||
                                (msg.from_phone ? `Unknown number ${formatPhoneDisplay(msg.from_phone) || msg.from_phone}` : "Unknown")}
                            </span>
                            <span className="msg-time">{when(msg.created_at)}</span>
                          </div>
                          <div className="msg-text">
                            {isOpen || !long ? msg.text || (msg.photos ? "" : "—") : `${msg.text.slice(0, PREVIEW)}…`}
                          </div>
                          <div className="msg-item-meta">
                            {msg.order_number && <span>#{msg.order_number}</span>}
                            {msg.location_name && (
                              <span>{shortLocationName(msg.location_name)}</span>
                            )}
                            {msg.service_writer && (
                              <span>{shortName(msg.service_writer)}</span>
                            )}
                            {msg.source === "chariot" ? <span>Chariot text</span> : msg.origin && <span>{msg.origin}</span>}
                            {msg.photos > 0 && <span>{msg.photos === 1 ? "1 photo" : `${msg.photos} photos`}</span>}
                            {msg.dig_reply && <span className="msg-tag">Dig reply</span>}
                            {msg.unverified && <span className="msg-tag msg-tag-warn" title="Twilio's signature on this text didn't check out">Not verified</span>}
                          </div>
                        </div>
                        <button
                          className={`msg-read-btn${msg.is_read ? " is-read" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRead(msg, !msg.is_read);
                          }}
                          title={
                            msg.is_read
                              ? `Read by ${msg.acknowledged_by_name || "someone"} — click to mark unread`
                              : "Mark as read"
                          }
                        >
                          {msg.is_read ? "◉" : "○"}
                        </button>
                        {isOpen && (
                          <div className="msg-item-more">
                            <TextPhotos messageId={msg.message_id} count={msg.photos} />
                            <div className="msg-item-actions">
                              {msg.order_id && (
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => go(`/app/orders/${msg.order_id}`)}>
                                  Open order #{msg.order_number}
                                </button>
                              )}
                              {msg.customer_id && can("customers.access") && (
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => go(`/app/customers/${msg.customer_id}`)}>
                                  Open customer
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
